/**
 * Seedable PRNG. PLAN.md hard requirement: the sampler owns ALL randomness, the core
 * stays seed-free, and **same seed -> identical stream**. `Math.random` is not seedable
 * and is BANNED in the engine — use this.
 *
 * Implementation: xoshiro128** (Blackman & Vigna), a fast, well-distributed 32-bit
 * generator with a 128-bit state. The numeric seed is expanded into that state with
 * SplitMix32 so that even seed 0 produces a full-entropy state and consecutive seeds
 * (0, 1, 2, ...) yield decorrelated streams.
 *
 * All arithmetic is forced through `| 0` / `>>> 0` so it stays in 32-bit integer space
 * and is deterministic across V8 / workerd / node.
 */

export interface Rng {
  /** Next 32-bit unsigned integer in [0, 2^32). */
  nextU32(): number;
  /** Next float in [0, 1) with 32 bits of precision. */
  nextFloat(): number;
  /** Integer in [0, n) with no modulo bias (rejection sampling). `n` must be in [1, 2^32]. */
  nextInt(n: number): number;
}

/** SplitMix32 — expands a single 32-bit seed into a stream used to fill larger state. */
function splitMix32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x9e3779b9) | 0;
    let z = s;
    z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
    z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
    return (z ^ (z >>> 15)) >>> 0;
  };
}

function rotl(x: number, k: number): number {
  return ((x << k) | (x >>> (32 - k))) >>> 0;
}

/**
 * Create a seeded xoshiro128** generator from a numeric seed.
 *
 * @param seed any number; only the low 32 bits are used. The same seed always yields
 *             the identical stream.
 */
export function createRng(seed: number): Rng {
  const mix = splitMix32(seed >>> 0);
  // Fill the 128-bit state; the loop guarantees a non-zero state (xoshiro requires it).
  let s0 = mix();
  let s1 = mix();
  let s2 = mix();
  let s3 = mix();
  if ((s0 | s1 | s2 | s3) === 0) {
    s0 = 0x9e3779b9;
  }

  function nextU32(): number {
    const result = (Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7) >>> 0, 9) >>> 0) >>> 0;
    const t = (s1 << 9) >>> 0;
    s2 = (s2 ^ s0) >>> 0;
    s3 = (s3 ^ s1) >>> 0;
    s1 = (s1 ^ s2) >>> 0;
    s0 = (s0 ^ s3) >>> 0;
    s2 = (s2 ^ t) >>> 0;
    s3 = rotl(s3, 11);
    return result;
  }

  function nextFloat(): number {
    // 32-bit mantissa scaled into [0, 1).
    return nextU32() / 4294967296;
  }

  function nextInt(n: number): number {
    if (!Number.isInteger(n) || n < 1 || n > 0x100000000) {
      throw new RangeError(`nextInt bound must be an integer in [1, 2^32], got ${n}`);
    }
    if (n === 0x100000000) {
      return nextU32();
    }
    // Rejection sampling to avoid modulo bias. Keep `limit` as a plain JS double in
    // [1, 2^32]: a `>>> 0` here would wrap an exact 2^32 (when `n` is a power of two, so
    // `2^32 % n === 0`) down to 0, making `x >= limit` always true and looping forever.
    const limit = 0x100000000 - (0x100000000 % n);
    let x = nextU32();
    while (x >= limit) {
      x = nextU32();
    }
    return x % n;
  }

  return { nextU32, nextFloat, nextInt };
}
